import React, { useEffect, useState } from "react";
import './Expression.scss'
import { Table } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import hosts from '../Hosts/animals.json';
import { pathogen } from '../Hosts/pathogen.js';
import { useParams } from "react-router-dom";
import { env } from '../../env';
import axios from "axios";

export default function Expression() {
    const { host: hostUnderscores, pathogen: pathogenUnderscores, experiment, fdr, fold } = useParams();
    const [imagePaths, setImagePaths] = useState([]);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [paginatedData, setPaginatedData] = useState([]);

    const itemsPerPage = 100;

    const getHostCommonName = () => {
        return hosts.find(item => item.sname === hostUnderscores.replace('_', ' ')).name;
    };

    const getPathogenSpecies = () => {
        return pathogen[pathogenUnderscores];
    };

    const getExperimentURL = () => {
        if (experiment.startsWith('GSE')) {
            return 'https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=' + experiment;
        } else {
            return 'https://www.ebi.ac.uk/biostudies/arrayexpress/studies/' + experiment;
        }
    };

    const checkGraph = async (folder, graphType) => {
        const response = await fetch(`${env.BACKEND}/api/expression/checkFileExists?folder=${folder}&fileType=${graphType}`);
        if (response.status === 200) {
            let imageUrls = await response.json();
            if (imageUrls.length === 0) {
                imageUrls = null;
            }
            setImagePaths(prevState => ({
                ...prevState,
                [graphType]: imageUrls
            }));
        } else {
            setImagePaths(prevState => ({
                ...prevState,
                [graphType]: null
            }));
        }
    };

    const checkFolder = (folder) => {
        const url = `${env.BACKEND}/api/expression/checkFolderExists?folder=${folder}`;
        return axios
            .get(url)
            .then((res) => {
                return res.data.exists;
            });
    }

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
        const newPaginatedData = data.slice(selected * itemsPerPage, (selected + 1) * itemsPerPage);
        setPaginatedData(newPaginatedData);
    };

    const loadData = (folder, folderParams) => {
        checkGraph(folder, 'ModuleMembershipGeneSignificance');
        checkGraph(folder, 'Dendrogram');
        checkGraph(folder, 'ClusteringDendrogram');
        checkGraph(folder, 'GeneClusteringTOM');
        checkGraph(folder, 'HeatmapCounts');
        checkGraph(folder, 'HeatmapDEGs');
        checkGraph(folder, 'Heatmap');
        checkGraph(folder, 'CPM_PCA');
        checkGraph(folder, 'MRC_PCA');
        checkGraph(folder, 'ClusteringEigengenes');
        checkGraph(folder, 'Clust');
        checkGraph(folderParams, 'FilteredDEG');
        checkGraph(folderParams, 'MA');
        checkGraph(folderParams, 'Volcano');
        checkGraph(folderParams, 'Venn');
        fetch(`${process.env.PUBLIC_URL}/expression/${hostUnderscores}_${pathogenUnderscores}/${experiment}/counts.txt`)
            .then(response => response.text())
            .then(text => {
                const rows = text.trim().split('\n');
                const headers = rows[0].split(',');
                const parsedData = rows.slice(1).map(row => row.split(','));
                setColumns(headers);
                setData(parsedData);
                setLoading(false);
                setPageCount(Math.ceil(parsedData.length / itemsPerPage));
                setPaginatedData(parsedData.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage));
                console.log('EFFECT')
            })
            .catch(error => console.error('Error fetching file:', error));
    }

    useEffect(() => {
        const folder = `${hostUnderscores}_${pathogenUnderscores}/${experiment}`;
        const folderParams = `${folder}/FDR_${fdr}_FOLD_${fold}`;
        checkFolder(folderParams)
            .then((exists) => {
                if (exists) {
                    loadData(folder, folderParams);
                } else {
                    const postBody = {
                        host: hostUnderscores,
                        pathogen: pathogenUnderscores,
                        experiment: experiment,
                        fdr: fdr,
                        fold: fold,
                    }
                    axios
                        .post(`${env.BACKEND}/api/expression/calculateExpression/`, postBody)
                        .then((res) => {
                            if (res.data) {
                                loadData(folder, folderParams);
                            } else {
                                alert('Sorry, there was an error. Please, contact us.')
                            }
                        })
                        .catch((err) => {
                            console.error(`Request for experiment ${experiment} failed`, err.response);
                        })
                }
            });
    // eslint-disable-next-line
    }, []);

    const pathogenName = getPathogenSpecies();
    const hostCommon = getHostCommonName();
    const url = getExperimentURL();

    return (
        <div className="container expression-container">
            <br></br>
            <h3>Expression for {hostCommon} infected with <i>{pathogenName}</i></h3>
            <ul className="nav nav-tabs justify-content-center" id="expression" role="tablist">
                <li className="nav-item">
                    <button className="nav-link active" id="table-tab" data-bs-toggle="tab" data-bs-target="#table" type="button" role="tab" aria-controls="table" aria-selected="true">Raw Counts</button>
                </li>
                {imagePaths['FilteredDEG'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="deg-tab" data-bs-toggle="tab" data-bs-target="#deg" type="button" role="tab" aria-controls="deg" aria-selected="false">Filtered DEG</button>
                    </li>
                )}
                {imagePaths['ModuleMembershipGeneSignificance'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="mmgs-tab" data-bs-toggle="tab" data-bs-target="#mmgs" type="button" role="tab" aria-controls="mmgs" aria-selected="false">Module Membership-Gene Significance</button>
                    </li>
                )}
                {imagePaths['Dendrogram'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="dendrogram-tab" data-bs-toggle="tab" data-bs-target="#dendrogram" type="button" role="tab" aria-controls="dendrogram" aria-selected="false">Dendrogram and Trait Heatmap</button>
                    </li>
                )}
                {imagePaths['ClusteringDendrogram'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="clusteringdendrogram-tab" data-bs-toggle="tab" data-bs-target="#clusteringdendrogram" type="button" role="tab" aria-controls="clusteringdendrogram" aria-selected="false">Clustering Dendrogram</button>
                    </li>
                )}
                {imagePaths['GeneClusteringTOM'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="tom-tab" data-bs-toggle="tab" data-bs-target="#tom" type="button" role="tab" aria-controls="tom" aria-selected="false">Gene Clustering (TOM-Based)</button>
                    </li>
                )}
                {imagePaths['HeatmapCounts'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="hmcounts-tab" data-bs-toggle="tab" data-bs-target="#hmcounts" type="button" role="tab" aria-controls="hmcounts" aria-selected="false">Heatmap (Top 50 Counts)</button>
                    </li>
                )}
                {imagePaths['HeatmapDEGs'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="hmdegs-tab" data-bs-toggle="tab" data-bs-target="#hmdegs" type="button" role="tab" aria-controls="hmdegs" aria-selected="false">Heatmap (Top 50 DEGs)</button>
                    </li>
                )}
                {imagePaths['Heatmap'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="hmmt-tab" data-bs-toggle="tab" data-bs-target="#hmmt" type="button" role="tab" aria-controls="hmmt" aria-selected="false">Heatmap (Module-Trait)</button>
                    </li>
                )}

                {imagePaths['CPM_PCA'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="cpmpca-tab" data-bs-toggle="tab" data-bs-target="#cpmpca" type="button" role="tab" aria-controls="cpmpca" aria-selected="false">PCA (Counts Per Million)</button>
                    </li>
                )}
                {imagePaths['MRC_PCA'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="mrcpca-tab" data-bs-toggle="tab" data-bs-target="#mrcpca" type="button" role="tab" aria-controls="mrcpca" aria-selected="false">PCA (Mean Ratio Count)</button>
                    </li>
                )}
                {imagePaths['ClusteringEigengenes'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="eigen-tab" data-bs-toggle="tab" data-bs-target="#eigen" type="button" role="tab" aria-controls="eigen" aria-selected="false">Clustering (Module Eigengenes)</button>
                    </li>
                )}
                {imagePaths['MA'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="ma-tab" data-bs-toggle="tab" data-bs-target="#ma" type="button" role="tab" aria-controls="ma" aria-selected="false">MA Plot</button>
                    </li>
                )}
                {imagePaths['Clust'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="clust-tab" data-bs-toggle="tab" data-bs-target="#clust" type="button" role="tab" aria-controls="clust" aria-selected="false">Co-Expression</button>
                    </li>
                )}

                {imagePaths['Venn'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="venn-tab" data-bs-toggle="tab" data-bs-target="#venn" type="button" role="tab" aria-controls="venn" aria-selected="false">Venn</button>
                    </li>
                )}
                {imagePaths['Volcano'] && (
                    <li className="nav-item">
                        <button className="nav-link" id="volcano-tab" data-bs-toggle="tab" data-bs-target="#volcano" type="button" role="tab" aria-controls="volcano" aria-selected="false">Volcano</button>
                    </li>
                )}
            </ul>
            <div className="tab-content" id="expressionContent">
                <br></br>
                <div className="tab-pane fade show active" id="table" role="tabpanel" aria-labelledby="table-tab">
                    <div className="expression-container">
                        <div className="row justify-content-center">
                            <div className="col-md-3">
                                <a className="btn kbl-btn-1" href={`${process.env.PUBLIC_URL}/expression/${hostUnderscores}_${pathogenUnderscores}/${experiment}/counts.txt`} download="counts.txt">
                                    Download Raw Counts File
                                </a>
                            </div>
                            <div className="col-md-3">
                                <a className="btn kbl-btn-1" href={`${process.env.PUBLIC_URL}/expression/${hostUnderscores}_${pathogenUnderscores}/${experiment}/input.txt`} download="identifiers.txt">
                                    Download Group Identifiers File
                                </a>
                            </div>
                            <div className="col-md-3">
                                <a className="btn kbl-btn-1" href={`${process.env.PUBLIC_URL}/expression/${hostUnderscores}_${pathogenUnderscores}/${experiment}/FDR_${fdr}_FOLD_${fold}/Filtered_downDEGs.xlsx`} download="Filtered_downDEGs.xlsx">
                                    Download Downregulated Genes
                                </a>
                            </div>
                            <div className="col-md-3">
                                <a className="btn kbl-btn-1" href={`${process.env.PUBLIC_URL}/expression/${hostUnderscores}_${pathogenUnderscores}/${experiment}/FDR_${fdr}_FOLD_${fold}/Filtered_upDEGs.xlsx`} download="Filtered_upDEGs.xlsx">
                                    Download Upregulated Genes
                                </a>
                            </div>
                        </div>
                        <br></br>
                        <div className="row justify-content-center">
                            <div className="col-md-3">
                                <a target="_blank" rel="noreferrer" href={`${url}`}>
                                    Experiment Webpage
                                </a>
                            </div>
                        </div>
                        <br></br>
                        {loading && (
                            <div className="flex justify-center">Loading...</div>
                        )}
                        {!loading && (
                            <>
                                <Table responsive className="kbl-table table-borderless">
                                    <thead className="kbl-thead">
                                        <tr>
                                            {columns.map((result, index) => (
                                                <th key={index + 1}>{result}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedData.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {row.map((cell, colIndex) => (
                                                    <td key={colIndex}>
                                                        {cell}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <ReactPaginate
                                    previousLabel={"<"}
                                    nextLabel={">"}
                                    breakLabel={"..."}
                                    breakClassName={"break-me"}
                                    pageCount={pageCount}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={handlePageClick}
                                    containerClassName={"pagination"}
                                    subContainerClassName={"pages pagination"}
                                    activeClassName={"active"}
                                    ellipsisItem={null}
                                />
                            </>
                        )}

                    </div>
                </div>

                {imagePaths['FilteredDEG'] && imagePaths['FilteredDEG'].length > 0 && (
                    <div className="tab-pane fade" id="deg" role="tabpanel" aria-labelledby="deg-tab">
                        {imagePaths['FilteredDEG'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Filtered DEG ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['ModuleMembershipGeneSignificance'] && imagePaths['ModuleMembershipGeneSignificance'].length > 0 && (
                    <div className="tab-pane fade" id="mmgs" role="tabpanel" aria-labelledby="mmgs-tab">
                        {imagePaths['ModuleMembershipGeneSignificance'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Module membership-Gene significance ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['Dendrogram'] && imagePaths['Dendrogram'].length > 0 && (
                    <div className="tab-pane fade" id="dendrogram" role="tabpanel" aria-labelledby="dendrogram-tab">
                        {imagePaths['Dendrogram'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Dendrogram and Trait Heatmap ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['ClusteringDendrogram'] && imagePaths['ClusteringDendrogram'].length > 0 && (
                    <div className="tab-pane fade" id="clusteringdendrogram" role="tabpanel" aria-labelledby="clusteringdendrogram-tab">
                        {imagePaths['ClusteringDendrogram'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Clustering Dendrogram ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['GeneClusteringTOM'] && imagePaths['GeneClusteringTOM'].length > 0 && (
                    <div className="tab-pane fade" id="tom" role="tabpanel" aria-labelledby="tom-tab">
                        {imagePaths['GeneClusteringTOM'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Gene Clustering (TOM-Based) ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['HeatmapCounts'] && imagePaths['HeatmapCounts'].length > 0 && (
                    <div className="tab-pane fade" id="hmcounts" role="tabpanel" aria-labelledby="hmcounts-tab">
                        {imagePaths['HeatmapCounts'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Heatmap (Top 50 Counts) ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['HeatmapDEGs'] && imagePaths['HeatmapDEGs'].length > 0 && (
                    <div className="tab-pane fade" id="hmdegs" role="tabpanel" aria-labelledby="hmdegs-tab">
                        {imagePaths['HeatmapDEGs'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Heatmap (Top 50 DEGs) ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['Heatmap'] && imagePaths['Heatmap'].length > 0 && (
                    <div className="tab-pane fade" id="hmmt" role="tabpanel" aria-labelledby="hmmt-tab">
                        {imagePaths['Heatmap'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Heatmap (Module-Trait) ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['CPM_PCA'] && imagePaths['CPM_PCA'].length > 0 && (
                    <div className="tab-pane fade" id="cpmpca" role="tabpanel" aria-labelledby="cpmpca-tab">
                        {imagePaths['CPM_PCA'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`PCA (Counts per Million) ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['MRC_PCA'] && imagePaths['MRC_PCA'].length > 0 && (
                    <div className="tab-pane fade" id="mrcpca" role="tabpanel" aria-labelledby="mrcpca-tab">
                        {imagePaths['MRC_PCA'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`PCA (Mean Ratio Count) ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['ClusteringEigengenes'] && imagePaths['ClusteringEigengenes'].length > 0 && (
                    <div className="tab-pane fade" id="eigen" role="tabpanel" aria-labelledby="eigen-tab">
                        {imagePaths['ClusteringEigengenes'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Clustering (Module Eigengenes) ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['MA'] && imagePaths['MA'].length > 0 && (
                    <div className="tab-pane fade" id="ma" role="tabpanel" aria-labelledby="ma-tab">
                        {imagePaths['MA'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`MA Plot ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['Clust'] && imagePaths['Clust'].length > 0 && (
                    <div className="tab-pane fade" id="clust" role="tabpanel" aria-labelledby="clust-tab">
                        <div className="row justify-content-center">
                            <div className="col-md-3">
                                <a className="btn kbl-btn-1" href={`${process.env.PUBLIC_URL}/expression/${hostUnderscores}_${pathogenUnderscores}/${experiment}/Clusters_Objects.tsv`} download="cluster_genes.txt">
                                    Download Gene List Per Cluster
                                </a>
                            </div>
                        </div>
                        {imagePaths['Clust'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Co-Expression ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['Venn'] && imagePaths['Venn'].length > 0 && (
                    <div className="tab-pane fade" id="venn" role="tabpanel" aria-labelledby="venn-tab">
                        {imagePaths['Venn'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Venn Diagram ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
                {imagePaths['Volcano'] && imagePaths['Volcano'].length > 0 && (
                    <div className="tab-pane fade" id="volcano" role="tabpanel" aria-labelledby="volcano-tab">
                        {imagePaths['Volcano'].map((path, index) => (
                            <div key={index}>
                                <img key={index} src={`${process.env.PUBLIC_URL}${path}`} style={{ "maxWidth": "100%", "height": "auto" }} alt={`Volcano Plot ${index}`} />
                                <div className="ant-divider ant-divider-horizontal" role="separator"></div>
                            </div>
                        ))}
                    </div>
                )}
            </div>


        </div>
    );
}
